<template>
  <div>
    <Loading :isLoading="isLoading" />
    <div class="d-flex mb-4">
      <div class="me-4">
        <div
          class="rounded-circle bg-light d-flex justify-content-center align-items-center"
          style="width: 100px; height: 100px; border: 1px solid #dee2e6"
        >
          <template v-if="logoUrl">
            <img
              :src="logoUrl"
              alt="Company Logo"
              class="img-fluid rounded-circle"
              style="max-width: 100px; max-height: 100px"
            />
          </template>
          <template v-else>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              fill="currentColor"
              class="bi bi-person text-secondary"
              viewBox="0 0 16 16"
            >
              <path
                d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"
              />
            </svg>
          </template>
        </div>
      </div>
      <div>
        <h6 class="mb-2">Upload Logo</h6>
        <p class="text-muted mb-3" style="font-size: 14px">
          Use a photo or image of your Company Logo. Use square ratio 1:1, and
          smaller than 2MB.
        </p>
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          @change="handleFileUpload"
          accept="image/*"
        />
        <button
          class="btn btn-outline-secondary"
          @click="$refs.fileInput.click()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-upload me-1"
            viewBox="0 0 16 16"
          >
            <path
              d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
            />
            <path
              d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"
            />
          </svg>
          Upload File
        </button>
      </div>
    </div>

    <!-- Basic Information Section -->
    <div class="card mb-4 bordered-card">
      <div
        class="card-header"
        style="
          background-color: #e8f5e9;
          border-bottom: none;
          padding: 0.75rem 1rem;
        "
      >
        <h6 class="mb-0 font-weight-bold text-dark">Basic Information</h6>
      </div>
      <div class="card-body">
        <table class="table table-borderless">
          <tbody>
            <tr>
              <td class="text-label">Company Name</td>
              <td class="text-value">{{ companyName }}</td>
            </tr>
            <tr>
              <td class="text-label">Business Registration Number (NIB)</td>
              <td class="text-value">{{ businessRegistrationNumber }}</td>
            </tr>
            <tr>
              <td class="text-label">Incorporation Date</td>
              <td class="text-value">{{ formatDate(incorporationDate) }}</td>
            </tr>
            <tr>
              <td class="text-label">Company Tax ID</td>
              <td class="text-value">{{ companyTaxId }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Company Address Section -->
    <div class="card mb-4 bordered-card">
      <div
        class="card-header"
        style="
          background-color: #e8f5e9;
          border-bottom: none;
          padding: 0.75rem 1rem;
        "
      >
        <h6 class="mb-0 font-weight-bold text-dark">Company Address</h6>
      </div>
      <div class="card-body">
        <table class="table table-borderless">
          <tbody>
            <tr>
              <td class="text-label">Address</td>
              <td class="text-value">{{ address }}</td>
            </tr>
            <tr>
              <td class="text-label">State/Province</td>
              <td class="text-value">{{ stateProvince }}</td>
            </tr>
            <tr>
              <td class="text-label">City</td>
              <td class="text-value">{{ city }}</td>
            </tr>
            <tr>
              <td class="text-label">Postal Code</td>
              <td class="text-value">{{ postalCode }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- User Information Section -->
    <div class="card mb-4 bordered-card">
      <div
        class="card-header"
        style="
          background-color: #e8f5e9;
          border-bottom: none;
          padding: 0.75rem 1rem;
        "
      >
        <h6 class="mb-0 font-weight-bold text-dark">User Information</h6>
      </div>
      <div class="card-body">
        <table class="table table-borderless">
          <tbody>
            <tr>
              <td class="text-label">Fullname</td>
              <td class="text-value">{{ fullname }}</td>
            </tr>
            <tr>
              <td class="text-label">Email</td>
              <td class="text-value">{{ email }}</td>
            </tr>
            <tr>
              <td class="text-label">Title</td>
              <td class="text-value">{{ title }}</td>
            </tr>
            <tr>
              <td class="text-label">Identity Type</td>
              <td class="text-value">{{ identityType }}</td>
            </tr>
            <tr>
              <td class="text-label">Photo Identity</td>
              <td class="text-value">
                <div v-if="photoIdentity" class="id-preview">
                  <img
                    :src="photoIdentity"
                    alt="Identity Photo"
                    style="max-height: 100px; cursor: pointer"
                    @click="showFullSizeImage(photoIdentity)"
                  />
                </div>
                <span v-else>No identity photo provided</span>
              </td>
            </tr>
            <tr>
              <td class="text-label">Identity Number</td>
              <td class="text-value">{{ identityNumber }}</td>
            </tr>
            <tr>
              <td class="text-label">Date of Birth</td>
              <td class="text-value">{{ formatDate(dateOfBirth) }}</td>
            </tr>
            <tr>
              <td class="text-label">Address</td>
              <td class="text-value">{{ address }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Bank Account Section -->
    <div class="card mb-4 bordered-card">
      <div
        class="card-header"
        style="
          background-color: #e8f5e9;
          border-bottom: none;
          padding: 0.75rem 1rem;
        "
      >
        <h6 class="mb-0 font-weight-bold text-dark">Bank Account</h6>
      </div>
      <div class="card-body">
        <table class="table table-borderless">
          <tbody>
            <tr>
              <td class="text-label">Bank</td>
              <td class="text-value">{{ bank }}</td>
            </tr>
            <tr>
              <td class="text-label">Account Number</td>
              <td class="text-value">{{ accountNumber }}</td>
            </tr>
            <tr>
              <td class="text-label">Account Name</td>
              <td class="text-value">{{ accountName }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Full-size image modal -->
    <div
      v-if="showModal"
      class="modal fade show"
      tabindex="-1"
      role="dialog"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Identity Document</h5>
            <button
              type="button"
              class="btn-close"
              @click="showModal = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body text-center">
            <img
              :src="fullSizeImage"
              class="img-fluid"
              alt="Full size identity document"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { uploadAvatar } from "@/api/company";

export default {
  name: "DetailInformation",
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const logoUrl = ref("");
    const showModal = ref(false);
    const fullSizeImage = ref("");
    const fileInput = ref(null);
    const isLoading = ref(false);

    const companyName = computed(() => props.data.company_name || "");
    const businessRegistrationNumber = computed(() => props.data.nib || "");
    const incorporationDate = computed(
      () => props.data.incorporation_date || ""
    );
    const companyTaxId = computed(() => props.data.npwp_number || "");
    const address = computed(() => props.data.address || "");
    const stateProvince = computed(() => props.data.state || "");
    const city = computed(() => props.data.city || "");
    const postalCode = computed(() => props.data.postcode || "");
    const fullname = computed(() => props.data.fullname || "");
    const email = computed(() => props.data.email || "");
    const title = computed(() => props.data.title || "");
    const identityType = computed(() => props.data.identity_type || "");
    const photoIdentity = computed(() => props.data.identity_photo || "");
    const identityNumber = computed(() => props.data.identity_number || "");
    const dateOfBirth = computed(() => props.data.date_of_birth || "");
    const bank = computed(() => props.data.bank_name || "");
    const accountNumber = computed(() => props.data.bank_account_number || "");
    const accountName = computed(() => props.data.bank_account_name || "");

    watch(
      () => props.data,
      (newData) => {
        if (newData) {
          logoUrl.value = newData.avatar_url || "";
        }
      },
      { immediate: true }
    );

    const formatDate = (dateString) => {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error("Error formatting date:", error);
        return dateString;
      }
    };

    const showFullSizeImage = (imageUrl) => {
      fullSizeImage.value = imageUrl;
      showModal.value = true;
    };

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 2 * 1024 * 1024) {
        alert("File size should be less than 2MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      const formData = new FormData();
      formData.append("avatar", file);

      isLoading.value = true; // Show loading
      try {
        const response = await uploadAvatar(formData);
        if (response.status === "success") {
          logoUrl.value = response.data.avatar_url;
          this.$swal("Success", "Logo uploaded successfully", "success");
        } else {
          this.$swal("Error", "Failed to upload logo", "error");
        }
      } catch (error) {
        this.$swal(
          "Error",
          "An error occurred while uploading the logo",
          "error"
        );
      } finally {
        isLoading.value = false;
      }
    };

    return {
      companyName,
      businessRegistrationNumber,
      incorporationDate,
      companyTaxId,
      address,
      stateProvince,
      city,
      postalCode,
      fullname,
      email,
      title,
      identityType,
      photoIdentity,
      identityNumber,
      dateOfBirth,
      bank,
      accountNumber,
      accountName,
      logoUrl,
      showModal,
      fullSizeImage,
      fileInput,
      formatDate,
      showFullSizeImage,
      handleFileUpload,
      isLoading,
    };
  },
};
</script>

<style scoped>
.text-label {
  font-family: "IBM Plex Sans Devanagari";
  font-size: 14px;
  font-weight: 400;
  width: 30%;
  line-height: 100%;
  text-align: right;
  color: #3f585a;
  padding: 0.5rem 1rem;
}

.text-value {
  font-family: "IBM Plex Sans Devanagari";
  color: #000;
  line-height: 100%;
  font-size: 16px;
  font-weight: 400;
  padding: 0.5rem 1rem;
}

.id-preview img {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  transition: opacity 0.2s;
}

.id-preview img:hover {
  opacity: 0.8;
}

.modal {
  z-index: 1050;
}

.bordered-card {
  box-shadow: none !important;
  border: 1px solid #dae0e2;
}
</style>
